import { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

interface PaymentStatus {
  id: string;
  status: 'pending' | 'succeeded' | 'cancelled' | 'waiting_for_capture';
  amount: number;
  currency: string;
  tier?: string;
  tokens?: number;
  period?: string;
  created_at: string;
  paid_at?: string;
}

/**
 * Hook для проверки статуса оплаты
 * Автоматически проверяет pending платежи при загрузке
 * Запускает polling для автоматического обновления данных
 */
export const usePaymentCheck = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const onPaymentSuccessRef = useRef<(() => void) | null>(null);
  const isInitializedRef = useRef(false);

  /**
   * Проверить статус конкретного платежа
   */
  const checkPaymentStatus = useCallback(async (paymentId: string): Promise<PaymentStatus | null> => {
    try {
      if (!hasAuthData()) {
        return null;
      }

      const data = await apiClient.payment.checkStatus(paymentId);

      if (data.success) {
        return data.payment;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, []);

  /**
   * Остановить polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  /**
   * Обработать успешную оплату
   */
  const handleSuccessfulPayment = useCallback(async (status: PaymentStatus) => {
    stopPolling();

    localStorage.removeItem('pending_payment_id');
    localStorage.removeItem('user-storage');
    localStorage.removeItem('subscription-limits-storage');

    window.dispatchEvent(new Event('storage'));

    if (onPaymentSuccessRef.current) {
      await onPaymentSuccessRef.current();
    }

    window.dispatchEvent(new CustomEvent('payment-success', {
      detail: { status }
    }));
  }, [stopPolling]);

  /**
   * Проверить pending платеж из localStorage
   */
  const checkPendingPayment = useCallback(async () => {
    const pendingPaymentId = localStorage.getItem('pending_payment_id');
    
    if (!pendingPaymentId) {
      return;
    }

    setIsChecking(true);

    try {
      const status = await checkPaymentStatus(pendingPaymentId);

      if (status) {
        setPaymentStatus(status);

        if (status.status === 'succeeded') {
          await handleSuccessfulPayment(status);
        } else if (status.status === 'cancelled') {
          stopPolling();
          localStorage.removeItem('pending_payment_id');
        }
      }
    } catch (error) {
      // Ignore errors
    } finally {
      setIsChecking(false);
    }
  }, [checkPaymentStatus, handleSuccessfulPayment, stopPolling]);

  /**
   * Начать polling для pending платежа
   */
  const startPolling = useCallback((paymentId: string) => {
    // Если уже идет polling, не запускаем новый
    if (pollingIntervalRef.current) {
      return;
    }
    
    setIsPolling(true);
    
    // Проверяем сразу
    checkPaymentStatus(paymentId).then(status => {
      if (status) {
        setPaymentStatus(status);
        if (status.status === 'succeeded') {
          handleSuccessfulPayment(status);
          return;
        } else if (status.status === 'cancelled') {
          stopPolling();
          localStorage.removeItem('pending_payment_id');
          return;
        }
      }
    });
    
    // Запускаем интервал проверки каждые 5 секунд
    pollingIntervalRef.current = window.setInterval(async () => {
      const status = await checkPaymentStatus(paymentId);
      
      if (status) {
        setPaymentStatus(status);
        
        if (status.status === 'succeeded') {
          await handleSuccessfulPayment(status);
        } else if (status.status === 'cancelled') {
          stopPolling();
          localStorage.removeItem('pending_payment_id');
        }
      }
    }, 5000);
    
  }, [checkPaymentStatus, handleSuccessfulPayment, stopPolling]);

  /**
   * Сохранить ID платежа для последующей проверки
   */
  const savePendingPayment = useCallback((paymentId: string) => {
    localStorage.setItem('pending_payment_id', paymentId);
    startPolling(paymentId);
  }, [startPolling]);

  /**
   * Очистить pending платёж
   */
  const clearPendingPayment = useCallback(() => {
    stopPolling();
    localStorage.removeItem('pending_payment_id');
    setPaymentStatus(null);
  }, [stopPolling]);

  /**
   * Установить callback для успешной оплаты
   */
  const setOnPaymentSuccess = useCallback((callback: () => void) => {
    onPaymentSuccessRef.current = callback;
  }, []);

  // Автоматическая проверка при монтировании (только один раз!)
  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const checkOnMount = async () => {
      // Даем время на инициализацию Telegram WebApp
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const pendingPaymentId = localStorage.getItem('pending_payment_id');
      if (pendingPaymentId) {
        setIsChecking(true);
        try {
          const status = await checkPaymentStatus(pendingPaymentId);
          if (status) {
            setPaymentStatus(status);
            if (status.status === 'succeeded') {
              await handleSuccessfulPayment(status);
            } else if (status.status === 'cancelled') {
              localStorage.removeItem('pending_payment_id');
            } else if (status.status === 'pending') {
              // Запускаем polling только если ещё не запущен
              if (!pollingIntervalRef.current) {
                startPolling(pendingPaymentId);
              }
            }
          }
        } finally {
          setIsChecking(false);
        }
      }
    };

    checkOnMount();
    
    // Cleanup при размонтировании
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [checkPaymentStatus, handleSuccessfulPayment, startPolling]);

  return {
    isChecking,
    isPolling,
    paymentStatus,
    checkPaymentStatus,
    checkPendingPayment,
    savePendingPayment,
    clearPendingPayment,
    startPolling,
    stopPolling,
    setOnPaymentSuccess
  };
};
